import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

export const NahrainPercentageInput = ({
                                           className = '',
                                           initialValue = 10,
                                           onChange,
                                           label = '',
                                           min = 10,
                                           max = 100
                                       }) => {
    const [value, setValue] = useState(Math.max(min, Math.min(max, initialValue)));
    const [t] = useTranslation("global");
    const intervalRef = useRef(null);
    const buttonRef = useRef({ increment: null, decrement: null });

    // Cleanup intervals on unmount
    useEffect(() => {
        return () => {
            stopIncrementing();
        };
    }, []);

    useEffect(() => {
        onChange?.(value);
    }, [value, onChange]);

    const startIncrementing = (type) => {
        // Immediate first action
        type === 'increment' ? handleIncrement() : handleDecrement();

        // Then start interval
        intervalRef.current = setInterval(() => {
            type === 'increment' ? handleIncrement() : handleDecrement();
        }, 150); // Adjust speed as needed
    };

    const stopIncrementing = () => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
    };

    const handleIncrement = () => {
        setValue(prev => Math.min(max, prev + 1));
    };

    const handleDecrement = () => {
        setValue(prev => Math.max(min, prev - 1));
    };

    const handleInputChange = (e) => {
        const newValue = parseInt(e.target.value) || min;
        if (!isNaN(newValue)) {
            setValue(Math.max(min, Math.min(max, newValue)));
        }
    };

    return (
        <div className={`${className} flex flex-col gap-2 w-fit`}>
            {label && <label className="text-onBackgroundCaption">{label}</label>}
            <div className="flex items-center border border-strokeGray rounded-lg overflow-hidden">
                <button
                    ref={el => buttonRef.current.decrement = el}
                    className="px-4 py-3 bg-primary text-onPrimary disabled:opacity-80
                    active:bg-primaryDark transition-colors"
                    onClick={handleDecrement}
                    onMouseDown={() => startIncrementing('decrement')}
                    onMouseUp={stopIncrementing}
                    onMouseLeave={stopIncrementing}
                    onTouchStart={() => startIncrementing('decrement')}
                    onTouchEnd={stopIncrementing}
                    disabled={value <= min}
                    type="button"
                >
                    -
                </button>

                <div className="flex-1 py-3 px-4">
                    <input
                        type="number"
                        className="bg-transparent w-8 text-onBackground text-center focus:outline-none"
                        value={value}
                        min={min}
                        max={max}
                        onChange={handleInputChange}
                    />
                    <span className={`text-onBackgroundCaption`}>%</span>
                </div>

                <button
                    ref={el => buttonRef.current.increment = el}
                    className="px-4 py-3 bg-primary text-onPrimary disabled:opacity-80
                    active:bg-primaryDark transition-colors"
                    onClick={handleIncrement}
                    onMouseDown={() => startIncrementing('increment')}
                    onMouseUp={stopIncrementing}
                    onMouseLeave={stopIncrementing}
                    onTouchStart={() => startIncrementing('increment')}
                    onTouchEnd={stopIncrementing}
                    disabled={value >= max}
                    type="button"
                >
                    +
                </button>
            </div>
        </div>
    );
};